import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
    Users,
    MapPin,
    Phone,
    Mail,
    Trash2,
    Eye,
    Search,
    ChevronLeft,
    ChevronRight,
    Calendar,
    LayoutGrid,
    List,
    SlidersHorizontal,
} from "lucide-react";
import { getBuyers, deleteBuyer } from "../../api/buyer";
import { syncVirtualContact } from "../../api/contact";
import ContactNotesButton from "../../features/contacts/components/ContactNotesButton";
import BuyerPreferencesModal from "../../features/contacts/components/BuyerPreferencesModal";
import toast from "react-hot-toast";
import { capitalizeEachWord } from "../../helper/function";
import { apiCache, cacheKeys, cachedApiCall, invalidateCache } from "../../lib/cache";
import DeleteConfirmationModal from "../common/DeleteConfirmationModal";

const BuyerManagement = () => {
    const navigate = useNavigate();
    const [buyers, setBuyers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [skeletonLoading, setSkeletonLoading] = useState(false);
    const [openingProfile, setOpeningProfile] = useState(false);
    const [viewType, setViewType] = useState(localStorage.getItem("buyerViewType") || "grid");


    // Pagination & Search states
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [activeCount, setActiveCount] = useState(0);
    const [deactivatedCount, setDeactivatedCount] = useState(0);
    const [recentlyPurchasedCount, setRecentlyPurchasedCount] = useState(0);
    const [recentlyRentedCount, setRecentlyRentedCount] = useState(0);
    // Which buyers the list shows: 'active' (default, excludes Recently Purchased
    // and Recently rented), 'recently_purchased' (CRM-2026-00054) or
    // 'recently_rented' (CRM-2026-00132).
    const [statusFilter, setStatusFilter] = useState('active');
    const [pageSize] = useState(12);
    const [searchQuery, setSearchQuery] = useState("");
    const searchTimeout = useRef(null);

    // Delete Modal state
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [buyerToDelete, setBuyerToDelete] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    // Buyer whose buying preferences are being edited (null = closed).
    const [prefsBuyer, setPrefsBuyer] = useState(null);

    // Brand Colors
    const brandColors = {
        primary: "#575955",
        secondary: "#55C5D0",
    };

    // Fetch buyers from API with caching
    const fetchBuyers = async (page = 1, search = "", forceRefresh = false) => {
        try {
            const params = {
                page,
                page_size: pageSize,
                status: statusFilter,
                ...(search && { search })
            };

            // Only show skeleton loading on first load (when no cache exists)
            if (!apiCache.has(cacheKeys.buyers(params)) || forceRefresh) {
                setSkeletonLoading(true);
            }

            // Use enhanced caching with stale-while-revalidate
            const res = await cachedApiCall(
                cacheKeys.buyers(params),
                () => getBuyers(params),
                30 * 1000, // 30 second cache for better accuracy
                { forceRefresh }
            );

            const data = res.data;

            // Handle paginated response
            if (data.results) {
                setBuyers(data.results || []);
                setTotalCount(data.count || 0);
                setTotalPages(Math.ceil((data.count || 0) / pageSize));
                if (data.active_count !== undefined) setActiveCount(data.active_count);
                if (data.deactivated_count !== undefined) setDeactivatedCount(data.deactivated_count);
                if (data.recently_purchased_count !== undefined) setRecentlyPurchasedCount(data.recently_purchased_count);
                if (data.recently_rented_count !== undefined) setRecentlyRentedCount(data.recently_rented_count);
            } else {
                // Fallback for non-paginated response
                setBuyers(Array.isArray(data) ? data : []);
                setTotalCount(Array.isArray(data) ? data.length : 0);
                setTotalPages(1);
            }

            setSkeletonLoading(false);
        } catch (error) {
            setSkeletonLoading(false);
            console.error("Failed to fetch buyers:", error);
            toast.error("Failed to fetch buyers.");
        }
    };

    // Initial load - force refresh to ensure we see new buyers created externally
    useEffect(() => {
        fetchBuyers(currentPage, searchQuery, true);
    }, [currentPage]);

    // Switching the Active / Recently Purchased view refetches from page 1.
    useEffect(() => {
        if (currentPage !== 1) { setCurrentPage(1); return; }
        fetchBuyers(1, searchQuery, true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusFilter]);

    // Handle search with debounce
    const handleSearch = (value) => {
        setSearchQuery(value);

        // Clear previous timeout
        if (searchTimeout.current) {
            clearTimeout(searchTimeout.current);
        }

        // Debounce search - wait 500ms after user stops typing
        searchTimeout.current = setTimeout(() => {
            setCurrentPage(1); // Reset to first page on new search
            fetchBuyers(1, value);
        }, 500);
    };

    // Pagination handlers
    const handlePreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    const handleNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };

    const handlePageClick = (page) => {
        setCurrentPage(page);
    };

    const handleDelete = (buyer) => {
        setBuyerToDelete(buyer);
        setIsDeleteModalOpen(true);
    };

    const confirmDelete = async () => {
        if (!buyerToDelete) return;

        setDeleteLoading(true);
        try {
            await deleteBuyer(buyerToDelete.id);

            // Immediately remove buyer from UI state
            setBuyers(prevBuyers => prevBuyers.filter(buyer => buyer.id !== buyerToDelete.id));
            setTotalCount(prev => Math.max(0, prev - 1));

            toast.success("User deleted successfully");

            // Invalidate cache and refresh to get accurate data
            invalidateCache.buyers();

            // Refresh after a short delay to ensure backend is updated
            setTimeout(() => {
                fetchBuyers(currentPage, searchQuery, true);
            }, 500);

            setIsDeleteModalOpen(false);
            setBuyerToDelete(null);
        } catch (error) {
            toast.error(error?.response?.data?.detail || "Failed to delete user");
            console.error(error);

            // Refresh on error to restore correct state
            fetchBuyers(currentPage, searchQuery, true);
        } finally {
            setDeleteLoading(false);
        }
    };


    const getBuyerStatus = (buyer) => {
        if (buyer.is_active) return { label: 'active', className: 'bg-green-100 text-green-700' };
        if (buyer.is_deactivated) return { label: 'deactivated', className: 'bg-amber-100 text-amber-700' };
        return { label: 'inactive', className: 'bg-gray-100 text-gray-700' };
    };

    // Format date helper
    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const openBuyerProfile = async (buyer) => {
        if (openingProfile || !buyer?.id) return;
        setOpeningProfile(true);
        const displayName = buyer.name ? capitalizeEachWord(buyer.name) : buyer.email;
        const toastId = toast.loading(`Opening ${displayName}'s profile...`);
        try {
            const res = await syncVirtualContact(`buyer_${buyer.id}`);
            const synced = res?.data ?? res;
            const contactId = synced?.id ?? synced?.pk;
            if (contactId == null) throw new Error('Could not resolve contact');
            apiCache.invalidatePattern('contacts-');
            toast.dismiss(toastId);
            navigate(`/contacts/${contactId}`);
        } catch (err) {
            toast.error(
                err?.response?.data?.error || err?.message || 'Could not open buyer profile.',
                { id: toastId }
            );
        } finally {
            setOpeningProfile(false);
        }
    };

    // --- UI ---
    return (
        <div className="p-8 space-y-8">
            {/* Header. This page has no sidebar tab any more - it is reached
                from the "Portal Users" card on Contacts (Monday 13058000755),
                so it carries its own way back. */}
            <div className="flex justify-between items-center">
                <div>
                    <button
                        type="button"
                        onClick={() => navigate('/contacts')}
                        className="inline-flex items-center gap-1 text-sm font-medium text-gray-500 hover:text-gray-700 mb-2"
                    >
                        <ChevronLeft className="w-4 h-4" />
                        Back to Contacts
                    </button>
                    <h2 className="text-3xl font-semibold text-gray-900">Portal Users</h2>
                    <p className="text-gray-600 text-base mt-1">
                        Manage buyer portal users and their accounts
                    </p>
                </div>
            </div>

            {/* Search Bar & View Toggle */}
            <div className="bg-white rounded-2xl shadow border border-gray-100 p-4">
                <div className="flex flex-col md:flex-row gap-4 items-center">
                    <div className="relative flex-1 w-full">
                        <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                        <input
                            type="text"
                            placeholder="Search users by name, email, phone, or address..."
                            value={searchQuery}
                            onChange={(e) => handleSearch(e.target.value)}
                            className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent"
                        />
                    </div>
                    <div className="flex bg-gray-100 p-1 rounded-xl">
                        <button
                            onClick={() => {
                                setViewType("grid");
                                localStorage.setItem("buyerViewType", "grid");
                            }}
                            className={`p-2 rounded-lg transition-all ${viewType === "grid"
                                ? "bg-white shadow-sm text-[#55C5D0]"
                                : "text-gray-500 hover:text-gray-700"
                                }`}
                            title="Grid View"
                        >
                            <LayoutGrid className="w-5 h-5" />
                        </button>
                        <button
                            onClick={() => {
                                setViewType("list");
                                localStorage.setItem("buyerViewType", "list");
                            }}
                            className={`p-2 rounded-lg transition-all ${viewType === "list"
                                ? "bg-white shadow-sm text-[#55C5D0]"
                                : "text-gray-500 hover:text-gray-700"
                                }`}
                            title="List View"
                        >
                            <List className="w-5 h-5" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 gap-6">
                <div className="bg-white rounded-2xl shadow border border-gray-100 p-6 flex flex-col items-start">
                    <div className="flex items-center justify-between w-full mb-2">
                        <Users className="w-6 h-6" style={{ color: brandColors.primary }} />
                        <span className="text-gray-500 text-sm font-medium">
                            Total Users
                        </span>
                    </div>
                    <span className="text-2xl font-bold text-gray-900">
                        {totalCount}
                    </span>
                </div>
                <button
                    type="button"
                    onClick={() => setStatusFilter('active')}
                    title="Show active buyers"
                    className={`bg-white rounded-2xl shadow border p-6 flex flex-col items-start text-left transition-all ${statusFilter === 'active' ? 'border-[#22c55e] ring-2 ring-[#22c55e]/30' : 'border-gray-100 hover:border-gray-300'}`}
                >
                    <div className="flex items-center justify-between w-full mb-2">
                        <Users className="w-6 h-6" style={{ color: "#22c55e" }} />
                        <span className="text-gray-500 text-sm font-medium">
                            Active Users
                        </span>
                    </div>
                    <span className="text-2xl font-bold text-gray-900">
                        {activeCount}
                    </span>
                </button>
                <button
                    type="button"
                    onClick={() => setStatusFilter('recently_purchased')}
                    title="Show buyers marked Recently Purchased"
                    className={`bg-white rounded-2xl shadow border p-6 flex flex-col items-start text-left transition-all ${statusFilter === 'recently_purchased' ? 'border-[#55C5D0] ring-2 ring-[#55C5D0]/30' : 'border-gray-100 hover:border-gray-300'}`}
                >
                    <div className="flex items-center justify-between w-full mb-2">
                        <Users className="w-6 h-6" style={{ color: "#55C5D0" }} />
                        <span className="text-gray-500 text-sm font-medium">
                            Recently Purchased
                        </span>
                    </div>
                    <span className="text-2xl font-bold text-gray-900">
                        {recentlyPurchasedCount}
                    </span>
                </button>
                <button
                    type="button"
                    onClick={() => setStatusFilter('recently_rented')}
                    title="Show buyers renting for now who will look to buy again later"
                    className={`bg-white rounded-2xl shadow border p-6 flex flex-col items-start text-left transition-all ${statusFilter === 'recently_rented' ? 'border-sky-500 ring-2 ring-sky-500/30' : 'border-gray-100 hover:border-gray-300'}`}
                >
                    <div className="flex items-center justify-between w-full mb-2">
                        <Users className="w-6 h-6 text-sky-500" />
                        <span className="text-gray-500 text-sm font-medium">
                            Recently Rented
                        </span>
                    </div>
                    <span className="text-2xl font-bold text-gray-900">
                        {recentlyRentedCount}
                    </span>
                </button>
                <div className="bg-white rounded-2xl shadow border border-gray-100 p-6 flex flex-col items-start">
                    <div className="flex items-center justify-between w-full mb-2">
                        <Users className="w-6 h-6" style={{ color: "#d97706" }} />
                        <span className="text-gray-500 text-sm font-medium">
                            Deactivated Users
                        </span>
                    </div>
                    <span className="text-2xl font-bold text-gray-900">
                        {deactivatedCount}
                    </span>
                </div>
            </div>

            {/* Users View */}
            {skeletonLoading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-6">
                    {Array.from({ length: 6 }).map((_, i) => (
                        <div
                            key={i}
                            className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 animate-pulse"
                        >
                            <div className="flex items-start space-x-4">
                                <div className="w-12 h-12 rounded-full bg-gray-200" />
                                <div className="flex-1 space-y-2">
                                    <div className="h-4 bg-gray-200 rounded w-3/4" />
                                    <div className="h-3 bg-gray-100 rounded w-1/2" />
                                </div>
                                <div className="w-16 h-6 bg-gray-100 rounded-full" />
                            </div>
                            <div className="mt-4 space-y-2">
                                <div className="h-3 bg-gray-100 rounded w-5/6" />
                                <div className="h-3 bg-gray-100 rounded w-4/6" />
                                <div className="h-3 bg-gray-100 rounded w-3/6" />
                            </div>
                            <div className="flex justify-end gap-2 mt-4">
                                <div className="w-8 h-8 bg-gray-200 rounded-full" />
                                <div className="w-8 h-8 bg-gray-200 rounded-full" />
                            </div>
                        </div>
                    ))}
                </div>
            ) : buyers.length > 0 ? (
                viewType === "grid" ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {buyers.map((buyer) => (
                            <div
                                key={buyer.id}
                                onClick={() => openBuyerProfile(buyer)}
                                className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 hover:shadow-md transition-all duration-300 relative flex flex-col cursor-pointer"
                                style={{ borderTop: `4px solid ${brandColors.secondary}` }}
                            >
                                <div className="flex items-center space-x-4 mb-2">
                                    <div
                                        className="w-12 h-12 rounded-full flex items-center justify-center text-white shadow-md text-lg font-semibold"
                                        style={{ backgroundColor: brandColors.primary }}
                                    >
                                        {buyer.name
                                            ? buyer.name.split(" ").map((n) => n[0]).join("")
                                            : buyer.email[0].toUpperCase()}
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="text-lg font-semibold text-gray-900 mb-0.5">
                                            {buyer.name ? capitalizeEachWord(buyer.name) : "No Name"}
                                        </h3>
                                        <div className="flex items-center gap-2">
                                            {(() => { const s = getBuyerStatus(buyer); return (
                                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${s.className}`}>
                                                    {s.label}
                                                </span>
                                            ); })()}
                                        </div>
                                    </div>
                                </div>
                                <div className="space-y-3 mb-4">
                                    <div className="flex items-center text-sm text-gray-600">
                                        <Mail className="w-4 h-4 mr-2" />
                                        {buyer.email}
                                    </div>
                                    <div className="flex items-center text-sm text-gray-600">
                                        <Phone className="w-4 h-4 mr-2" />
                                        {buyer.phone_number || "N/A"}
                                    </div>
                                    <div className="flex items-center text-sm text-gray-600">
                                        <MapPin className="w-4 h-4 mr-2" />
                                        {buyer.address || "No address"}
                                    </div>
                                    <div className="flex items-center text-sm text-gray-600">
                                        <Calendar className="w-4 h-4 mr-2" />
                                        Joined: {formatDate(buyer.date_joined)}
                                    </div>
                                    {buyer.is_recently_purchased && (
                                        <div className="flex items-center text-sm text-[#55C5D0] font-medium">
                                            <Calendar className="w-4 h-4 mr-2" />
                                            Purchased: {buyer.purchase_date ? formatDate(buyer.purchase_date) : "Date not set"}
                                        </div>
                                    )}
                                    {buyer.buyer_status === 'rented' && (
                                        <div className="flex items-center text-sm text-sky-600 font-medium">
                                            <Calendar className="w-4 h-4 mr-2" />
                                            Rented, back to buying: {buyer.buyer_paused_until ? formatDate(buyer.buyer_paused_until) : "Date not set"}
                                        </div>
                                    )}
                                </div>
                                <div className="flex justify-end gap-2 mt-auto" onClick={(e) => e.stopPropagation()}>
                                    <ContactNotesButton
                                        suggestPreferences
                                        identity={{ buyerId: buyer.id, email: buyer.email, name: buyer.name, phone: buyer.phone_number }}
                                        compact
                                        className="p-2 text-sm font-medium rounded-xl bg-amber-100 text-amber-700 hover:bg-amber-200 transition-all duration-200"
                                    />
                                    <button
                                        onClick={(e) => { e.stopPropagation(); setPrefsBuyer({ buyerId: buyer.id, email: buyer.email, name: buyer.name, phone: buyer.phone_number }); }}
                                        title="Edit buying preferences"
                                        className="p-2 text-sm font-medium rounded-xl bg-teal-100 text-teal-700 hover:bg-teal-200 transition-all duration-200"
                                    >
                                        <SlidersHorizontal className="w-4 h-4" />
                                    </button>
                                    <button
                                        onClick={(e) => { e.stopPropagation(); openBuyerProfile(buyer); }}
                                        className="p-2 text-sm font-medium rounded-xl bg-[#55C5D0]/10 text-[#55C5D0] hover:bg-[#55C5D0]/20 transition-all duration-200"
                                        title="View Profile"
                                    >
                                        <Eye className="w-4 h-4" />
                                    </button>
                                    <button
                                        onClick={(e) => { e.stopPropagation(); handleDelete(buyer); }}
                                        className="p-2 text-sm font-medium rounded-xl bg-red-100 text-red-700 hover:bg-red-200 transition-all duration-200"
                                        title="Delete"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="bg-white rounded-2xl shadow border border-gray-100 overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full text-left">
                                <thead className="bg-gray-50 border-b border-gray-100">
                                    <tr>
                                        <th className="px-6 py-4 text-sm font-semibold text-gray-900">User</th>
                                        <th className="px-6 py-4 text-sm font-semibold text-gray-900 text-nowrap">Joined Date</th>
                                        <th className="px-6 py-4 text-sm font-semibold text-gray-900 text-nowrap">Phone</th>
                                        <th className="px-6 py-4 text-sm font-semibold text-gray-900 text-nowrap">Status</th>
                                        <th className="px-6 py-4 text-sm font-semibold text-gray-900 text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {buyers.map((buyer) => (
                                        <tr key={buyer.id} onClick={() => openBuyerProfile(buyer)} className="hover:bg-gray-50 transition-colors cursor-pointer">
                                            <td className="px-6 py-4">
                                                <div className="flex items-center space-x-3">
                                                    <div
                                                        className="w-10 h-10 rounded-full flex items-center justify-center text-white shadow-sm font-medium shrink-0"
                                                        style={{ backgroundColor: brandColors.primary }}
                                                    >
                                                        {buyer.name
                                                            ? buyer.name.split(" ").map((n) => n[0]).join("")
                                                            : buyer.email[0].toUpperCase()}
                                                    </div>
                                                    <div className="min-w-0">
                                                        <p className="text-sm font-semibold text-gray-900 truncate">
                                                            {buyer.name ? capitalizeEachWord(buyer.name) : "No Name"}
                                                        </p>
                                                        <p className="text-xs text-gray-500 truncate">{buyer.email}</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-600">
                                                {formatDate(buyer.date_joined)}
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-600">
                                                {buyer.phone_number || "N/A"}
                                            </td>
                                            <td className="px-6 py-4 text-sm">
                                                {(() => { const s = getBuyerStatus(buyer); return (
                                                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${s.className}`}>
                                                        {s.label}
                                                    </span>
                                                ); })()}
                                                {buyer.is_recently_purchased && (
                                                    <div className="mt-1 text-xs text-[#55C5D0] font-medium text-nowrap">
                                                        Purchased: {buyer.purchase_date ? formatDate(buyer.purchase_date) : "Date not set"}
                                                    </div>
                                                )}
                                                {buyer.buyer_status === 'rented' && (
                                                    <div className="mt-1 text-xs text-sky-600 font-medium text-nowrap">
                                                        Rented, back to buying: {buyer.buyer_paused_until ? formatDate(buyer.buyer_paused_until) : "Date not set"}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                <div className="flex justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                                                    <ContactNotesButton
                                                        suggestPreferences
                                                        identity={{ buyerId: buyer.id, email: buyer.email, name: buyer.name, phone: buyer.phone_number }}
                                                        compact
                                                        className="p-2 text-gray-500 hover:text-amber-600 transition-colors"
                                                    />
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); setPrefsBuyer({ buyerId: buyer.id, email: buyer.email, name: buyer.name, phone: buyer.phone_number }); }}
                                                        title="Edit buying preferences"
                                                        className="p-2 text-gray-500 hover:text-teal-600 transition-colors"
                                                    >
                                                        <SlidersHorizontal className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); openBuyerProfile(buyer); }}
                                                        className="p-2 text-gray-500 hover:text-[#55C5D0] transition-colors"
                                                        title="View Profile"
                                                    >
                                                        <Eye className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); handleDelete(buyer); }}
                                                        className="p-2 text-gray-500 hover:text-red-600 transition-colors"
                                                        title="Delete"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )
            ) : (
                <div className="text-center py-12">
                    <Users className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">
                        No Users Found
                    </h3>
                    <p className="mt-1 text-sm text-gray-500">
                        No user accounts have been created yet.
                    </p>
                </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="bg-white rounded-2xl shadow border border-gray-100 p-4">
                    <div className="flex items-center justify-between">
                        <div className="text-sm text-gray-600">
                            Showing {buyers.length > 0 ? (currentPage - 1) * pageSize + 1 : 0} to{" "}
                            {Math.min(currentPage * pageSize, totalCount)} of {totalCount} users
                        </div>
                        <div className="flex items-center space-x-2">
                            <button
                                onClick={handlePreviousPage}
                                disabled={currentPage === 1}
                                className="p-2 rounded-lg border border-gray-200 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                            >
                                <ChevronLeft className="w-5 h-5 text-gray-600" />
                            </button>

                            <div className="flex items-center space-x-1">
                                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                                    let pageNum;
                                    if (totalPages <= 5) {
                                        pageNum = i + 1;
                                    } else if (currentPage <= 3) {
                                        pageNum = i + 1;
                                    } else if (currentPage >= totalPages - 2) {
                                        pageNum = totalPages - 4 + i;
                                    } else {
                                        pageNum = currentPage - 2 + i;
                                    }

                                    return (
                                        <button
                                            key={pageNum}
                                            onClick={() => handlePageClick(pageNum)}
                                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${currentPage === pageNum
                                                ? "bg-[#55C5D0] text-white"
                                                : "border border-gray-200 text-gray-600 hover:bg-gray-50"
                                                }`}
                                        >
                                            {pageNum}
                                        </button>
                                    );
                                })}
                            </div>

                            <button
                                onClick={handleNextPage}
                                disabled={currentPage === totalPages}
                                className="p-2 rounded-lg border border-gray-200 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                            >
                                <ChevronRight className="w-5 h-5 text-gray-600" />
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            <DeleteConfirmationModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={confirmDelete}
                title="Delete User"
                description="Are you sure you want to delete this user account? This action cannot be undone."
                itemName={buyerToDelete?.name || buyerToDelete?.email}
                loading={deleteLoading}
            />

            {prefsBuyer && (
                <BuyerPreferencesModal
                    identity={prefsBuyer}
                    title={prefsBuyer.name}
                    onClose={() => setPrefsBuyer(null)}
                />
            )}

        </div>
    );
};

export default BuyerManagement;
